import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Meteor } from 'meteor/meteor';
import { useTracker } from 'meteor/react-meteor-data';
import { defer, formatDateTime } from '/imports/utils';
import { Icon } from '/imports/ui/Icon';
import { useFind, useFindOne, useSubscribe } from '/imports/hooks';
import { Publications } from '/imports/publications';
import { Notifications, Notification, markNotificationsRead } from '/imports/api/notifications';
import { ScrollDiv } from '/imports/ui/ScrollDiv';
import { toastOnError } from '/imports/ui/utils';
import { NotificationsCount } from '/imports/collections';

export const NotificationDrawer = () => {
  const [open, setOpen] = useState(false);
  const [limit, setLimit] = useState(10);
  useSubscribe(Publications.notificationsCount);
  const loading = useTracker(() => {
    const handle = Meteor.subscribe(Publications.notifications, { limit });
    return !handle.ready();
  }, [limit]);
  const notifications = useFind(() => Notifications.find({}, { sort: { createdAt: -1 } }), [loading]);
  useEffect(() => {
    if (open) {
      document.body.addEventListener('click', () => {
        defer(() => {setOpen(false); markRead();});
      }, { once: true });
    }
  }, [open]);
  const numUnread = useFindOne(() => NotificationsCount.find('_id'), [open]);
  const markRead = toastOnError(async () => {
    await markNotificationsRead.call();
  });
  return (
    <div className="user-dropdown relative flex">
      <div
        className="toggler relative box-border flex cursor-pointer px-4 mobile:min-w-0 [&_.badge]:absolute [&_.badge]:top-0 [&_.badge]:right-0 [&_.badge]:flex [&_.badge]:h-6 [&_.badge]:w-6 [&_.badge]:flex-col [&_.badge]:items-center [&_.badge]:justify-center [&_.badge]:rounded-xl [&_.badge]:bg-primary [&_.badge]:text-center [&_.badge]:text-xs [&_.badge]:text-white"
        onClick={(e) => { e.stopPropagation(); setOpen(!open); open && markRead(); }}
      >
        <Icon icon="notifications" className="text-primary" />
        {numUnread?.count && <span className="badge">{numUnread.count}</span> || null}
      </div>
      <ScrollDiv
        className={`dropdown absolute -left-0.5 top-full z-1 box-border h-auto max-h-[calc(100vh-var(--spacing-app-header)-4px)] min-w-80 overflow-auto border-2 border-mid bg-panel ${open ? 'block' : 'hidden'}`}
        onScrollBottom={() => setLimit(limit + 10)}
      >
        {
          notifications.map((n) => <NotificationItem key={n._id} notification={n} />)
        }
      </ScrollDiv>
    </div>
  );
};

interface NotificationItemProps {
  notification: Notification
}

const notificationItemClass = 'item relative block cursor-pointer border-b border-soft p-4 no-underline hover:bg-page-alt [&.unread]:bg-primary/10 [&_.date]:text-xs [&_.date]:text-fg-3 [&_.title]:m-0 [&_.title]:text-base [&_.title]:font-bold [&_.title]:text-fg-1 [&_.body]:text-[0.85rem] [&_.body]:text-fg-1';

const NotificationItem = (props: NotificationItemProps) => {
  const { notification } = props;
  return (
    <Link className={`${notificationItemClass}${notification.isRead ? '' : ' unread'}`} to={notification.link || '#'}>
      <div className="date">{notification.createdAt && formatDateTime(notification.createdAt) || null}</div>
      <div className="title">{notification.title}</div>
      <div className="body">
        {notification.body}
      </div>
    </Link>
  );
};
